import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './Services.module.css';

const services = [
  {
    id: 1, icon: '🏗️', img: 'svc1',
    title: 'Renovations & Repairs',
    short: 'Full home and commercial renovations, structural repairs, and suite upgrades.',
    detail: 'From complete gut renovations to targeted repairs, we handle every phase with precision. Interior and exterior upgrades, basement suites, and structural fixes done right the first time.',
    tags: ['Residential', 'Commercial', 'Repairs'],
  },
  {
    id: 2, icon: '🪵', img: 'svc2',
    title: 'Framing',
    short: 'New construction framing, additions, load-bearing walls, and structural modifications.',
    detail: 'Precise wood and metal stud framing for new builds, home additions, and interior layout changes. We work with architects and engineers to ensure every frame is level, square, and code-compliant.',
    tags: ['Wood Frame', 'Metal Stud', 'Additions'],
  },
  {
    id: 3, icon: '🏠', img: 'svc3',
    title: 'Cladding & Siding',
    short: 'Exterior cladding systems including vinyl, wood, fibre cement and modern panel siding.',
    detail: 'We install and replace all types of exterior cladding — from traditional vinyl and wood siding to contemporary fibre cement and engineered panel systems. Weatherproof, energy-efficient, and beautiful.',
    tags: ['Vinyl', 'Wood', 'Fibre Cement'],
  },
  {
    id: 4, icon: '🔩', img: 'svc4',
    title: 'Soffit & Flashing',
    short: 'Professional soffit, fascia and flashing installation to protect your building envelope.',
    detail: 'Proper soffit and flashing are critical to keeping moisture out. We install and replace aluminum, vinyl, and wood soffit systems alongside custom flashing details at rooflines, windows, and transitions.',
    tags: ['Soffit', 'Fascia', 'Flashing'],
  },
  {
    id: 5, icon: '💧', img: 'svc5',
    title: 'Rainscreen & Waterproofing',
    short: 'Rainscreen assembly installation and full building envelope waterproofing systems.',
    detail: 'BC building code requires proper rainscreen systems for lasting protection. We install drainage mat, breathable membranes, and complete waterproofing assemblies on new and existing structures.',
    tags: ['Rainscreen', 'Membrane', 'Drainage Mat'],
  },
  {
    id: 6, icon: '🧱', img: 'svc6',
    title: 'Hardie Board & Cement Board',
    short: 'James Hardie fibre cement and cement board panel installation for lasting exteriors.',
    detail: 'James Hardie fibre cement siding offers unbeatable durability in BC\'s wet climate. We are experienced Hardie board and cement panel installers — from lap siding to shingles to panel systems.',
    tags: ['James Hardie', 'Cement Board', 'Panel Systems'],
  },
  {
    id: 7, icon: '🏛️', img: 'svc7',
    title: 'Stucco Repairs',
    short: 'Stucco patching, crack repair, re-coating and full stucco remediation.',
    detail: 'Cracked or failing stucco can lead to serious water damage. We diagnose, patch, and re-coat stucco systems, from minor crack repairs to full remediation and re-cladding with modern alternatives.',
    tags: ['Patching', 'Re-coat', 'Remediation'],
  },
  {
    id: 8, icon: '🪨', img: 'svc8',
    title: 'Natural Stone',
    short: 'Natural and manufactured stone veneer installation for interiors and exteriors.',
    detail: 'Add timeless character to your home with natural or manufactured stone. We install stone veneer on feature walls, fireplaces, exterior facades, and garden features with meticulous attention to detail.',
    tags: ['Stone Veneer', 'Interior', 'Exterior'],
  },
  {
    id: 9, icon: '🪚', img: null,
    title: 'Finishing Carpentry',
    short: 'Crown moulding, baseboards, trim, doors, built-ins and all finish carpentry work.',
    detail: 'The details make the difference. Our finishing carpentry services cover baseboard and crown installation, custom built-ins, door hanging, window trim, and all the fine details that elevate a space.',
    tags: ['Trim', 'Moulding', 'Built-ins'],
  },
];

const imgs = {
  svc1: require('../../assets/svc1.jpg'),
  svc2: require('../../assets/svc2.jpg'),
  svc3: require('../../assets/svc3.jpg'),
  svc4: require('../../assets/svc4.jpg'),
  svc5: require('../../assets/svc5.jpg'),
  svc6: require('../../assets/svc6.jpg'),
  svc7: require('../../assets/svc7.jpg'),
  svc8: require('../../assets/svc8.jpg'),
};

export default function Services() {
  const [active, setActive] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.06 });

  return (
    <section id="services" className={styles.section} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className={styles.label}><span className={styles.labelLine} />Our Services</span>
          <h2 className={styles.heading}>
            What We <span className={styles.accent}>Build & Fix</span>
          </h2>
          <p className={styles.sub}>
            From structural framing to the finest finishing details — we handle it all.
            Every service delivered with trade expertise, clean execution, and a quality guarantee.
          </p>
        </motion.div>

        <div className={styles.grid}>
          {services.map((svc, i) => (
            <motion.div
              key={svc.id}
              className={`${styles.card} ${active === svc.id ? styles.cardOpen : ''}`}
              initial={{ opacity: 0, y: 36 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.06 * i }}
              onClick={() => setActive(active === svc.id ? null : svc.id)}
            >
              {svc.img && imgs[svc.img] && (
                <div className={styles.cardImg}>
                  <img src={imgs[svc.img]} alt={svc.title} />
                </div>
              )}
              <div className={styles.cardBody}>
                <div className={styles.cardTop}>
                  <span className={styles.cardIcon}>{svc.icon}</span>
                  <span className={styles.cardNum}>{String(svc.id).padStart(2,'0')}</span>
                </div>
                <h3 className={styles.cardTitle}>{svc.title}</h3>
                <p className={styles.cardShort}>{svc.short}</p>
                <AnimatePresence>
                  {active === svc.id && (
                    <motion.p
                      className={styles.cardDetail}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {svc.detail}
                    </motion.p>
                  )}
                </AnimatePresence>
                <div className={styles.cardFooter}>
                  <div className={styles.tags}>
                    {svc.tags.map(t => <span key={t} className={styles.tag}>{t}</span>)}
                  </div>
                  <span className={styles.expand}>
                    {active === svc.id ? '− Less' : '+ More'}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className={styles.cta}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <p>Not sure what you need? We offer free on-site assessments.</p>
          <a
            href="#contact"
            className={styles.ctaBtn}
            onClick={e => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            Book a Free Assessment
          </a>
        </motion.div>
      </div>
    </section>
  );
}
